import { Literal, OptionalValidatorKit, ValidatorOrShape } from './types'
import { baseValidators } from './builtins'
import { optionalArrayOf, optionalize } from './helpers'
import { Validator } from '../public'

/**
 * Creates a validator for an object with optional properties.
 * @internal
 * 
 * @param shape - The shape to validate against
 * @returns A validator that allows undefined values
 */
function optionalObject(shape: ValidatorOrShape): Validator {
  return optionalize(shape)
}

/**
 * Collection of validators that allow undefined values.
 * These validators are available through the `v.opt` namespace.
 */
export const optionalValidators: OptionalValidatorKit = {
  /** Validates that a value is a string or undefined */
  isString: optionalize(baseValidators.isString),
  /** Validates that a value is a number or undefined */
  isNumber: optionalize(baseValidators.isNumber),
  /** Validates that a value is an integer or undefined */
  isInteger: optionalize(baseValidators.isInteger),
  /** Validates that a value is a boolean or undefined */
  isBoolean: optionalize(baseValidators.isBoolean),
  /** Validates that a value is a numeric string or undefined */
  isNumericString: optionalize(baseValidators.isNumericString),
  /** Validates that a value is an integer string or undefined */
  isIntegerString: optionalize(baseValidators.isIntegerString),
  /** Validates that a value is a boolean string or undefined */
  isBooleanString: optionalize(baseValidators.isBooleanString),
  /** Validates that a value is an array or undefined */
  isArray: optionalize(baseValidators.isArray),
  /** Creates a validator for an object with optional properties */
  object: optionalObject,
  /** Creates a validator for an array of optional values */
  array: optionalArrayOf,
  /** Validates that a value is one of the specified enum values */
  isEnum: (e: Record<string, string | number>) => optionalize(baseValidators.isEnum(e)),
  /** Validates that a value is a literal */
  literally: (expected: Literal) => optionalize(baseValidators.literally(expected)),
  /** Date and time related validators that allow undefined values */
  dt: {
    /** Validates that a value is a valid ISO date string or undefined */
    isISODateString: optionalize(baseValidators.dt.isISODateString)
  }
}